/**
 * "Atendimentos ativos agora", por departamento e por atendente.
 *
 * Ponto 16 da Gisele (20/10/2026): 'painel "Atendimentos ativos agora" com
 * duas colunas lado a lado: por departamento (ex.: Comercial, Suporte,
 * Implantação, TI, Implantação MAX, MAX Suporte) e por atendente. Cada linha
 * mostra a quantidade de atendimentos em andamento naquele momento.'
 *
 * ## Sai do dado que o painel JÁ tem, e não de um endpoint novo
 *
 * `/rooms/agents-panel` devolve, por operador, o `active_count` (conversas com
 * `status = 1` agora), o `service_group` e o `service_group_id`. É tudo o que
 * as duas colunas precisam.
 *
 * Isso não é economia de trabalho -- é o que impede o número de discordar da
 * lista logo abaixo dele. Um endpoint próprio contaria por conta própria, com
 * o seu próprio recorte de canal e o seu próprio instante, e a diferença
 * apareceria como "o indicador diz 7, a lista mostra 5". Esta base já pagou
 * esse defeito ("card diz 30, lista mostra 18", conserto de 06/10).
 *
 * Pelo mesmo motivo o recorte de CANAL não é refeito aqui: ele já está dentro
 * do `active_count` que a api mandou, porque o painel passa o canal escolhido
 * na requisição.
 *
 * ## Quem não tem atendimento agora fica FORA
 *
 * São 32 operadores no tenant da SysFar, e na maior parte do dia um punhado
 * deles está em conversa. Listar os 32 com zero enterra a informação -- e o
 * pedido é justamente saber onde há atendimento ACONTECENDO.
 *
 * Para nada parecer perdido, a tela mostra o total ao lado do título: a soma
 * das linhas bate com ele, e quem não aparece é zero.
 */

/** O rótulo de quem não tem departamento. */
export const SEM_DEPARTAMENTO = 'Sem departamento';

/** Quantas conversas um operador tem agora, com o campo ausente valendo zero. */
const ativosDo = (agente) => {
    const n = Number(agente?.active_count);

    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Da maior para a menor, e o empate pelo nome.
 *
 * Sem o desempate pelo nome, a ordem entre dois departamentos com a mesma
 * contagem dependeria da ordem de chegada da api -- e as linhas trocariam de
 * lugar a cada atualização do painel, de 8 em 8 segundos, com os números
 * iguais. A tela pareceria estar mudando sem motivo.
 */
const maisAtivosPrimeiro = (a, b) => (
    b.quantidade - a.quantidade || String(a.rotulo).localeCompare(String(b.rotulo), 'pt-BR')
);

/**
 * As linhas da coluna "por departamento".
 *
 * @param {Array<object>} agentes as linhas de `/rooms/agents-panel`
 * @returns {Array<{chave: string, rotulo: string, quantidade: number}>}
 */
export const porDepartamento = (agentes) => {
    const soma = new Map();

    (Array.isArray(agentes) ? agentes : []).forEach((agente) => {
        const ativos = ativosDo(agente);
        if (ativos === 0) return;

        /*
         * Operador SEM departamento entra, com rótulo próprio.
         *
         * Esconder seria esconder justamente o caso que alguém precisa ver: a
         * conversa está acontecendo e não está contada em departamento nenhum.
         * Foi um operador sem departamento que explicou o ponto 26 ("vários
         * operadores somem do Kanban do ADM").
         */
        const rotulo = String(agente?.service_group || '').trim() || SEM_DEPARTAMENTO;
        soma.set(rotulo, (soma.get(rotulo) || 0) + ativos);
    });

    return [...soma.entries()]
        .map(([rotulo, quantidade]) => ({ chave: rotulo, rotulo, quantidade }))
        .sort(maisAtivosPrimeiro);
};

/**
 * As linhas da coluna "por atendente".
 *
 * A chave é o id do operador, e não o nome: dois homônimos são duas pessoas, e
 * somá-los faria o painel mostrar um atendente com o dobro das conversas.
 */
export const porAtendente = (agentes) => (Array.isArray(agentes) ? agentes : [])
    .filter((agente) => ativosDo(agente) > 0)
    .map((agente) => ({
        chave: String(agente?.operator?.id ?? agente?.operator?.name ?? ''),
        rotulo: String(agente?.operator?.name || 'Sem nome'),
        quantidade: ativosDo(agente),
    }))
    .sort(maisAtivosPrimeiro);

/**
 * O total de conversas em andamento agora.
 *
 * Mostrado ao lado do título porque as duas colunas omitem o zero: é ele que
 * diz que nada ficou de fora. As duas somas têm de bater com este número --
 * são a MESMA conta, agrupada de dois jeitos.
 */
export const totalAtivoAgora = (agentes) => (Array.isArray(agentes) ? agentes : [])
    .reduce((soma, agente) => soma + ativosDo(agente), 0);
